import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import FAQSection, { guessTheCountryFaqs } from '../components/FAQSection';
import WorldMap from '../components/WorldMap';
import { flagSrcSet, flagUrl } from '../data/countries';
import {
  createStatementDeck,
  getCountryByCode,
} from '../data/countryMapStatements';
import { getCountryMapRank, pickWrongCountryRoast } from '../utils/countryMapRoasts';
import { getItem, setItem } from '../utils/safeStorage';
import '../styles/GuessTheCountry.css';

const HIGHSCORE_KEY = 'gtc-guess-country-highscore';
const REVEAL_MS = 900;

export default function GuessTheCountryPage() {
  const deckRef = useRef(createStatementDeck());
  const scoreRef = useRef(0);
  const statementRef = useRef(null);
  const timeoutRef = useRef(null);

  const [statement, setStatement] = useState(() => {
    const first = deckRef.current.shift();
    statementRef.current = first;
    return first;
  });
  const [score, setScore] = useState(0);
  const [highscore, setHighscore] = useState(0);
  const [phase, setPhase] = useState('playing');
  const [highlights, setHighlights] = useState([]);
  const [wrongLine, setWrongLine] = useState(null);
  const [resultRank, setResultRank] = useState(null);
  const [missedCountry, setMissedCountry] = useState(null);
  const [pickedCountry, setPickedCountry] = useState(null);

  scoreRef.current = score;
  statementRef.current = statement;

  useEffect(() => {
    const stored = Number(getItem(HIGHSCORE_KEY));
    if (Number.isFinite(stored) && stored > 0) setHighscore(stored);
  }, []);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const drawNext = useCallback((excludeId) => {
    if (!deckRef.current.length || (deckRef.current.length === 1 && deckRef.current[0].id === excludeId)) {
      deckRef.current = createStatementDeck(excludeId);
    }
    let next = deckRef.current.shift();
    if (next?.id === excludeId) {
      deckRef.current.push(next);
      next = deckRef.current.shift() || next;
    }
    return next;
  }, []);

  const startGame = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    deckRef.current = createStatementDeck();
    const first = drawNext();
    setStatement(first);
    setScore(0);
    scoreRef.current = 0;
    setPhase('playing');
    setHighlights([]);
    setWrongLine(null);
    setResultRank(null);
    setMissedCountry(null);
    setPickedCountry(null);
  }, [drawNext]);

  const handleCountryClick = useCallback((code) => {
    if (phase !== 'playing') return;
    const current = statementRef.current;
    if (!current) return;

    const correct = code === current.code;
    const answer = getCountryByCode(current.code);
    const picked = getCountryByCode(code);

    if (correct) {
      const nextScore = scoreRef.current + 1;
      setScore(nextScore);
      setHighlights([{ code, type: 'correct' }]);
      setWrongLine(null);
      setHighscore((best) => {
        if (nextScore <= best) return best;
        setItem(HIGHSCORE_KEY, String(nextScore));
        return nextScore;
      });
      setPhase('revealing');
      timeoutRef.current = setTimeout(() => {
        const next = drawNext(current.id);
        setStatement(next);
        setHighlights([]);
        setPhase('playing');
      }, REVEAL_MS);
      return;
    }

    setPickedCountry(picked);
    setMissedCountry(answer);
    setWrongLine(pickWrongCountryRoast());
    setResultRank(getCountryMapRank(scoreRef.current));
    setHighlights([
      { code, type: 'wrong' },
      { code: current.code, type: 'answer' },
    ]);
    setPhase('revealing');
    timeoutRef.current = setTimeout(() => {
      setPhase('gameover');
    }, REVEAL_MS);
  }, [drawNext, phase]);

  const seo = getPageSeo('guess-the-country');
  const structuredData = seo?.structuredData
    ? [seo.structuredData, buildFAQSchema(guessTheCountryFaqs)]
    : buildFAQSchema(guessTheCountryFaqs);

  return (
    <>
      <SeoManager
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        structuredData={structuredData}
      />

      <section className="gtc-game" aria-label="Guess the country map game">
        <h1 className="gtc-visually-hidden">Guess the Country: Click the Map</h1>

        <header className="gtc-header">
          <div className="gtc-score-block">
            <span className="gtc-score-number">{highscore}</span>
            <span className="gtc-score-label">Highscore</span>
          </div>
          <div className="gtc-score-block">
            <span className="gtc-score-number">{score}</span>
            <span className="gtc-score-label">Score</span>
          </div>
        </header>

        <div className="gtc-clue" aria-live="polite">
          <p className="gtc-clue-kicker">Which country?</p>
          <p className="gtc-clue-text">{statement?.text}</p>
          {phase === 'revealing' && highlights.some((item) => item.type === 'correct') && (
            <p className="gtc-clue-result is-correct">Correct!</p>
          )}
          {phase === 'revealing' && wrongLine && (
            <p className="gtc-clue-result is-wrong">{wrongLine}</p>
          )}
        </div>

        <WorldMap
          disabled={phase !== 'playing'}
          highlights={highlights}
          onCountryClick={handleCountryClick}
        />

        {phase === 'gameover' && (
          <div className="gtc-overlay" role="dialog" aria-labelledby="gtc-gameover-title">
            <div className="gtc-overlay-card">
              <h2 id="gtc-gameover-title">Game Over</h2>
              <p className="gtc-overlay-score">Score: {score}</p>
              <p className="gtc-overlay-best">Best: {highscore}</p>
              {missedCountry && (
                <div className="gtc-answer-row">
                  {pickedCountry && (
                    <p className="gtc-answer-miss">You clicked {pickedCountry.name}.</p>
                  )}
                  <div className="gtc-answer-flag">
                    <img
                      src={flagUrl(missedCountry.code)}
                      srcSet={flagSrcSet(missedCountry.code)}
                      alt={`${missedCountry.name} flag`}
                      width="48"
                      height="48"
                    />
                  </div>
                  <p className="gtc-answer-name">It was {missedCountry.name}.</p>
                </div>
              )}
              {resultRank && (
                <>
                  <div className="gtc-rank-row">
                    <span className="gtc-rank-label">Your Rank:</span>
                    <span className="gtc-rank-animal">{resultRank.animal}</span>
                  </div>
                  <p className="gtc-overlay-roast">{resultRank.message}</p>
                </>
              )}
              <button type="button" className="gtc-btn gtc-btn-replay" onClick={startGame}>
                Play Again
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="gtc-extras">
        <h2>How to Play Guess the Country</h2>
        <p>
          Read the statement, then click the matching country on the world map.
          Capitals, landmarks, and geography facts are all fair game. A correct
          click scores a point and serves the next clue. One miss ends the run.
        </p>
        <ul>
          <li>Zoom with the buttons, the scroll wheel, or pinch on a phone.</li>
          <li>Drag the map at any zoom, including 100%, to reach countries at the edges.</li>
          <li>Your best streak is saved in this browser.</li>
        </ul>
      </section>

      <FAQSection mode="guessTheCountry" />
    </>
  );
}
